import { Trans } from "@lingui/macro";
import { FailureApi } from "@smp/api";
import { UiBox, UiButton, UiLink, UiModuleError, UiTypography } from "@smp/ui";
import { Failure } from "@smp/utils";

import { SocialLinks } from "@/components/ui/social_links";
import { useContextLanguage } from "@/context/language";
import { ELocale } from "@/context/language/language.types";
import { useFailureTranslator } from "@/hooks/use_failure_translator";

import { IProps } from "./error_loading.types";

const socialPagesWhiteList = ["vk", "facebook"] as const;

export const ErrorLoading = ({ errorOrFailure }: IProps) => {
  const { failureTranslationGet } = useFailureTranslator();
  const { changeLanguage } = useContextLanguage();

  const isUpdating =
    errorOrFailure &&
    errorOrFailure instanceof FailureApi &&
    [
      "API__INTERNAL__502_BAD_GATEWAY",
      "API__INTERNAL__503_SERVICE_UNAVAILABLE",
      "API__INTERNAL__504_GATEWAY_TIMEOUT",
    ].includes(errorOrFailure.code);

  const ErrorTitleText = () => {
    if (isUpdating) {
      return <Trans>We are getting an update</Trans>;
    }

    if (
      errorOrFailure instanceof Failure ||
      errorOrFailure instanceof FailureApi
    ) {
      return <>{failureTranslationGet(errorOrFailure)}</>;
    }

    if (errorOrFailure instanceof Error) {
      return <>{errorOrFailure.message}</>;
    }

    return <Trans>Error loading application data.</Trans>;
  };

  const locales = [
    { title: "EN", value: ELocale.en },
    { title: "RU", value: ELocale.ru },
    { title: "ES", value: ELocale.es },
    { title: "PT", value: ELocale.pt },
    { title: "KK", value: ELocale.kk },
    { title: "RO", value: ELocale.ro },
    { title: "UK", value: ELocale.uk },
  ];

  return (
    <UiModuleError>
      <UiBox>
        <UiBox textAlign="center" mb={2}>
          <UiTypography variant="h4">
            <ErrorTitleText />
          </UiTypography>
        </UiBox>
        <UiBox display="flex" justifyContent="center" mb={4}>
          {locales.map((locale) => (
            <UiButton onClick={() => changeLanguage(locale.value)} size="small">
              {locale.title}
            </UiButton>
          ))}
        </UiBox>
        <UiBox mb={2} textAlign="center">
          <UiTypography>
            {isUpdating ? (
              <Trans>
                We are updating SMMplanner right now. Please give us a moment
                and come back later.
              </Trans>
            ) : (
              <Trans>
                In most cases this problem is solved by refreshing the page and
                clearing your cache. Read more about this in{" "}
                <UiLink
                  href={`https://faq.${window.location.hostname}/ru/articles/3772126-%D0%BE%D1%87%D0%B8%D1%81%D1%82%D0%BA%D0%B0-%D0%BA%D1%8D%D1%88%D0%B0`}
                >
                  our article
                </UiLink>
                .
              </Trans>
            )}
          </UiTypography>
        </UiBox>
        <UiBox>
          <SocialLinks
            BoxChildProps={{ m: 1 }}
            whiteList={socialPagesWhiteList}
          />
        </UiBox>
      </UiBox>
    </UiModuleError>
  );
};
